import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { IconButton, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import RestoreIcon from '@material-ui/icons/Restore';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import orderedCollectionStyles from './orderedCollectionStyles';
import AutoFillControl from './AutoFillControl';
import PinAction from './PinAction';
import useOrderedCollectionMotion from './useOrderedCollectionMotion';
import RowEditActions from './RowEditActions';

const MAX_VISIBLE_WORDS = 20;
const normalizeWord = (word) => String(word || '').trim().toLowerCase();

const S = {
  ...orderedCollectionStyles,
  row: {
    ...orderedCollectionStyles.row,
    gridTemplateColumns: '32px minmax(0, 1fr) auto 36px',
  },
  editableText: {
    ...orderedCollectionStyles.itemName,
    cursor: 'text',
    fontWeight: 500,
  },
  editInput: {
    minWidth: 0,
    minHeight: 36,
    padding: '6px 10px',
    border: '1px solid var(--kea-color-primary)',
    borderRadius: 8,
    color: '#1e2248',
    fontSize: 13,
  },
  hint: { padding: '0 20px 8px', color: '#a9455e', fontSize: 12 },
  duplicateHint: { padding: '0 20px 8px', color: '#9a6a1f', fontSize: 12 },
};

const SuggestionsEditor = ({
  pinnedWords = [],
  excludedWords = [],
  generatedWords = [],
  onChange,
  onExcludedChange = () => {},
  autoFill = true,
  onAutoFillChange = () => {},
  hideHeader = false,
}) => {
  const t = useLocalizedMessage();
  const inputRef = useRef(null);
  const editInputRef = useRef(null);
  const [addInput, setAddInput] = useState('');
  const [duplicateHint, setDuplicateHint] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);

  const excludedKeys = useMemo(
    () => new Set(excludedWords.map(normalizeWord)),
    [excludedWords],
  );

  const effectiveItems = useMemo(() => {
    const seen = new Set();
    const manual = [];
    pinnedWords.forEach((word) => {
      const key = normalizeWord(word);
      if (!key || seen.has(key) || manual.length >= MAX_VISIBLE_WORDS) return;
      manual.push({ key, word: String(word).trim(), manual: true });
      seen.add(key);
    });

    const automatic = [];
    if (autoFill) generatedWords.forEach((word) => {
      const key = normalizeWord(word);
      if (
        !key
        || seen.has(key)
        || excludedKeys.has(key)
        || manual.length + automatic.length >= MAX_VISIBLE_WORDS
      ) return;
      automatic.push({ key, word: String(word).trim(), manual: false });
      seen.add(key);
    });

    return [...manual, ...automatic];
  }, [autoFill, excludedKeys, generatedWords, pinnedWords]);
  const { containerRef: listMotionRef, animateNextChange } = useOrderedCollectionMotion(
    effectiveItems.map(item => item.key),
  );

  useEffect(() => {
    if (editingIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingIndex]);

  const handleDragEnd = useCallback((result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const moved = effectiveItems[result.source.index];
    if (!moved) return;
    if (moved.manual) {
      const nextPinned = [...pinnedWords];
      const [word] = nextPinned.splice(result.source.index, 1);
      const destinationIndex = Math.min(result.destination.index, nextPinned.length);
      if (destinationIndex !== result.destination.index) {
        animateNextChange({ itemKey: moved.key, fromIndex: result.destination.index });
      }
      nextPinned.splice(destinationIndex, 0, word);
      onChange(nextPinned);
      return;
    }
    const nextPinned = [...pinnedWords];
    const destinationIndex = Math.min(result.destination.index, nextPinned.length);
    if (destinationIndex !== result.destination.index) {
      animateNextChange({ itemKey: moved.key, fromIndex: result.destination.index });
    }
    nextPinned.splice(destinationIndex, 0, moved.word);
    onChange(nextPinned);
  }, [animateNextChange, effectiveItems, onChange, pinnedWords]);

  const handleRemove = useCallback((item) => {
    const key = normalizeWord(item.word);
    const nextManual = pinnedWords.filter((candidate) => normalizeWord(candidate) !== key);
    if (item.manual) {
      onChange(nextManual);
      return;
    }
    if (!excludedKeys.has(key)) onExcludedChange([...excludedWords, String(item.word).trim()]);
  }, [excludedKeys, excludedWords, onChange, onExcludedChange, pinnedWords]);

  const handlePin = useCallback((item) => {
    const key = normalizeWord(item.word);
    animateNextChange();
    if (item.manual) {
      onChange(pinnedWords.filter((candidate) => normalizeWord(candidate) !== key));
      return;
    }
    onChange([...pinnedWords, item.word]);
  }, [animateNextChange, onChange, pinnedWords]);

  const handleRestore = useCallback((word) => {
    const key = normalizeWord(word);
    onExcludedChange(excludedWords.filter((candidate) => normalizeWord(candidate) !== key));
  }, [excludedWords, onExcludedChange]);

  const commitAdd = useCallback(() => {
    const trimmed = addInput.trim();
    const key = normalizeWord(trimmed);
    if (!key) {
      setAddInput('');
      return;
    }
    if (effectiveItems.some((item) => item.key === key)) {
      setDuplicateHint(true);
      return;
    }
    if (excludedKeys.has(key)) handleRestore(trimmed);
    const next = pinnedWords.length >= MAX_VISIBLE_WORDS
      ? [...pinnedWords.slice(0, MAX_VISIBLE_WORDS - 1), trimmed]
      : [...pinnedWords, trimmed];
    onChange(next);
    setAddInput('');
    setDuplicateHint(false);
  }, [addInput, effectiveItems, excludedKeys, handleRestore, onChange, pinnedWords]);

  const startEdit = useCallback((index) => {
    setEditingIndex(index);
    setEditValue(effectiveItems[index].word);
    setEditError(false);
  }, [effectiveItems]);

  const cancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditValue('');
    setEditError(false);
  }, []);

  const commitEdit = useCallback(() => {
    const trimmed = editValue.trim();
    const key = normalizeWord(trimmed);
    if (
      !key
      || effectiveItems.some((item, index) => index !== editingIndex && item.key === key)
    ) {
      setEditError(true);
      return;
    }
    if (trimmed === effectiveItems[editingIndex].word) {
      cancelEdit();
      return;
    }
    const current = effectiveItems[editingIndex];
    if (current.manual) {
      const currentKey = normalizeWord(current.word);
      onChange(pinnedWords.map((word) => (
        normalizeWord(word) === currentKey ? trimmed : word
      )));
    } else {
      onChange([...pinnedWords, trimmed]);
      if (!excludedKeys.has(current.key)) {
        onExcludedChange([...excludedWords, current.word]);
      }
    }
    cancelEdit();
  }, [
    cancelEdit,
    editValue,
    editingIndex,
    effectiveItems,
    excludedKeys,
    excludedWords,
    onChange,
    onExcludedChange,
    pinnedWords,
  ]);

  const handleEditKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitEdit();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  }, [cancelEdit, commitEdit]);

  return (
    <div style={S.wrapper}>
      {!hideHeader && <h3>{t('empty.query.words.editor.title')}</h3>}

      <div style={S.addRow}>
        <input
          ref={inputRef}
          type="text"
          value={addInput}
          onChange={(event) => {
            setAddInput(event.target.value);
            setDuplicateHint(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',') {
              event.preventDefault();
              commitAdd();
            }
          }}
          placeholder={t('empty.query.words.editor.add.placeholder')}
          style={S.addInput}
        />
        <button type="button" onClick={commitAdd} style={S.addButton}>
          {t('empty.query.words.editor.add')}
        </button>
      </div>
      {duplicateHint && <div style={S.duplicateHint}>{t('empty.query.words.editor.duplicate')}</div>}

      <div style={S.toolbar}>
        <span style={S.toolbarSummary}>
          <span style={S.toolbarStrong}>{pinnedWords.length}</span>
          {' '}{t('empty.query.collection.pinned')}
          {' · '}{effectiveItems.length} {t('empty.query.collection.visible.of', { limit: MAX_VISIBLE_WORDS })}
        </span>
        <span style={S.toolbarActions}>
          <AutoFillControl
            enabled={autoFill}
            limit={MAX_VISIBLE_WORDS}
            unit={t('empty.query.autofill.suggestions')}
            onChange={onAutoFillChange}
          />
        </span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="effective-words">
          {(provided) => (
            <div
              ref={(node) => {
                provided.innerRef(node);
                listMotionRef.current = node;
              }}
              {...provided.droppableProps}
              style={S.zone}
            >
              {effectiveItems.length === 0 && (
                <div style={S.emptyState}>{t('empty.query.words.editor.generated.empty')}</div>
              )}
              {effectiveItems.map((item, index) => (
                <Draggable
                  key={`word-${item.key}`}
                  draggableId={`word-${item.key}`}
                  index={index}
                  isDragDisabled={editingIndex !== null}
                >
                  {(drag, snapshot) => (
                    <div
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      {...drag.dragHandleProps}
                      onMouseEnter={() => setHoveredKey(item.key)}
                      onMouseLeave={() => setHoveredKey(null)}
                      data-collection-row
                      data-collection-key={item.key}
                      style={{
                        ...S.row,
                        ...drag.draggableProps.style,
                        ...(editingIndex === index ? {
                          gridTemplateColumns: '32px minmax(0, 1fr) 76px',
                        } : null),
                        ...(item.manual ? S.rowPinned : null),
                        ...(hoveredKey === item.key ? { background: '#fbfefd' } : null),
                        ...(snapshot?.isDragging ? S.rowDragging : null),
                      }}
                    >
                      {item.manual ? (
                        <span style={S.pinnedPosition}>{index + 1}</span>
                      ) : (
                        <div
                          style={S.dragHandle}
                          aria-label={t('empty.query.words.editor.drag')}
                        >
                          <DragIndicatorIcon style={S.dragIcon} aria-hidden="true" />
                        </div>
                      )}

                      {editingIndex === index ? (
                        <>
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editValue}
                            onChange={(event) => {
                              setEditValue(event.target.value);
                              setEditError(false);
                            }}
                            onKeyDown={handleEditKeyDown}
                            style={S.editInput}
                            aria-label={t('empty.query.words.editor.row.edit.label', { word: item.word })}
                          />
                          <RowEditActions
                            confirmLabel={t('empty.query.words.editor.row.confirm')}
                            cancelLabel={t('empty.query.words.editor.row.cancel')}
                            onConfirm={commitEdit}
                            onCancel={cancelEdit}
                          />
                        </>
                      ) : (
                        <>
                          <span
                            role="button"
                            tabIndex={0}
                            title={item.word}
                            style={S.editableText}
                            onClick={() => startEdit(index)}
                            onKeyDown={(event) => event.key === 'Enter' && startEdit(index)}
                            aria-label={t('empty.query.words.editor.row.edit.label', { word: item.word })}
                          >
                            {item.word}
                          </span>
                          <PinAction
                            pinned={item.manual}
                            label={t(
                              item.manual
                                ? 'empty.query.collection.unpin.item'
                                : 'empty.query.collection.pin.item',
                              { name: item.word },
                            )}
                            onClick={() => handlePin(item)}
                          />
                          <Tooltip title={t(
                            item.manual
                              ? 'empty.query.collection.remove.item'
                              : 'empty.query.collection.block.item',
                            { name: item.word },
                          )}>
                            <IconButton
                              size="small"
                              style={{
                                ...S.actionButton,
                                opacity: hoveredKey === item.key ? 1 : 0.55,
                              }}
                              aria-label={t(
                                item.manual
                                  ? 'empty.query.collection.remove.item'
                                  : 'empty.query.collection.block.item',
                                { name: item.word },
                              )}
                              onClick={() => handleRemove(item)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {editError && <div style={S.hint}>{t('empty.query.words.editor.row.edit.error')}</div>}

      {excludedWords.length > 0 && (
        <div style={S.hiddenZone}>
          <div style={S.hiddenTitle}>{t('empty.query.collection.blocked')}</div>
          {excludedWords.map((word) => (
            <div key={`hidden-word-${normalizeWord(word)}`} style={S.hiddenRow}>
              <span title={word} style={S.hiddenText}>{word}</span>
              <Tooltip title={t('empty.query.words.editor.restore.label', { word })}>
                <IconButton
                  size="small"
                  style={S.actionButton}
                  aria-label={t('empty.query.words.editor.restore.label', { word })}
                  onClick={() => handleRestore(word)}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestionsEditor;
